//region :: Imports ::
import React, {createRef, ReactElement, ReactNode, RefObject} from 'react';
import MComponent from '../../Toolkit/Components/Component';
import {MCancelIconName, MIcon, MOkIconName} from '../../Toolkit/Icons';
import {DialogPositionType} from 'primereact/dialog';
import Dialog from '../../Toolkit/Components/Controls/Dialog';
import MButton from '../../Toolkit/Components/Controls/Button';
import MDropDown from '../../Toolkit/Components/Controls/DropDown';
import {MDropDownItem} from '../../Toolkit/Components/Controls/DropDownOption';
import {MNullable} from '../../Toolkit/Utils';
import MFormControl from '../../Toolkit/Components/Controls/FormControl';
import MCalendar from '../../Toolkit/Components/Controls/Calendar';
import {UploadIconName} from '../SafeDigConstants';
import MTextBox from '../../Toolkit/Components/Controls/TextBox';
import {SafeDigFile} from '../../Api/SafeDigFileTypes';

//endregion

/** */
type Props = {
    jobComplete: boolean;
    uploadPermission: boolean;
}

/** */
type State = {
    position?: DialogPositionType;
    visible: boolean;
    pageTitle: string;
    data: UploadData;
    fileInfoOptions: MDropDownItem<number>[];
    onUploadFile?: (data: UploadData) => void;
    uploading: boolean;
}

export type UploadData = {
    file: File | undefined;
    fileType: number | undefined;
    fileInfo: string | undefined;
    fileName: string | undefined
    expiryDate: Date | undefined;
    searchPositive: boolean | undefined;
}

/** */
class FileUploadDialog extends MComponent<Props, State> {

    private UploadIcon: MIcon = {name: UploadIconName, color: 'mcolor-info'};
    private readonly m_dateRef: RefObject<MCalendar>;

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_dateRef = createRef<MCalendar>();

        this.initializeState();
    }

    /** */
    private bindMethods(): void {
        this.dialog_onHide = this.dialog_onHide.bind(this);

        this.getDialogTemplate = this.getDialogTemplate.bind(this);
        this.onFileChange = this.onFileChange.bind(this);
        this.onFileInfoChange = this.onFileInfoChange.bind(this);
        this.onFileTypeChange = this.onFileTypeChange.bind(this);
        this.getFooterTemplate = this.getFooterTemplate.bind(this);
        this.setFileInfoOptions = this.setFileInfoOptions.bind(this);
        this.ok_Click = this.ok_Click.bind(this);

        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
    }

    /** */
    private initializeState(): void {
        this.state = {
            visible: false,
            fileInfoOptions: [],
            pageTitle: 'Line Search File Upload',
            uploading: false,
            data: {
                file: undefined,
                fileType: undefined,
                fileInfo: undefined,
                fileName: '',
                searchPositive: undefined,
                expiryDate: new Date()
            }
        };
    }

    /** */
    private dialog_onHide(): void {
        this.safeSetState((draft: State) => draft.visible = false);
    }

    /** */
    public show(inputCallback: (data: UploadData) => void, fileType: number, position?: DialogPositionType) {

        var initialDate = new Date();
        if(fileType === 1 || fileType === 2 || fileType === 3 || fileType === 5 || fileType === 6) {//Gas, Electric, Water, Telecoms or LSBUD
            initialDate.setDate(initialDate.getDate() + 28);
        } else if(fileType === 4 || fileType === 7){
            initialDate.setFullYear(initialDate.getFullYear() + 2);
        } else {
            initialDate.setDate(initialDate.getDate() + 90);
        }

        const searchDefault = (fileType === 4 || fileType === 6) ? true : false;

        this.safeSetState((draft: State) => {
            draft.position = position;
            draft.visible = true;
            draft.onUploadFile = inputCallback;
            draft.uploading = false;
            draft.data.fileType = fileType;
            draft.data.fileName = 'default';
            draft.data.expiryDate = initialDate;
            draft.data.searchPositive = searchDefault;
            draft.data.file = undefined;
        });

        this.setFileInfoOptions(fileType);

        if(fileType === 7) {
            this.safeSetState((draft: State) => draft.pageTitle = 'Document Upload');
        } else {
            this.safeSetState((draft: State) => draft.pageTitle = 'Line Search File Upload');
        }
    }

    public hide() {
        this.safeSetState((draft: State) => {
            draft.visible = false;
        });
    }

    private ok_Click() {
        if(!this.state.uploading){
            this.safeSetState((draft: State) => draft.uploading = true);
            this.state.onUploadFile!(this.state.data!);
        }
    }

    private getDialogTemplate(): ReactNode {
        const dropdownInitialValue = this.state.data.fileType === 4 || this.state.data.fileType === 6 ? 1 : 0;

        return (
            <div className="formgrid grid">
                <div className="col-12 md:col-12">
                    <MFormControl label={'File Type'} id={'FileType'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MDropDown<number> id="FileTypeDropdown"
                                                   disabled={true}
                                                   onChange={this.onFileTypeChange}
                                                   items={[{label: 'Gas', value: 1},
                                                       {label: 'Electric', value: 2},
                                                       {label: 'Water', value: 3},
                                                       {label: 'BAPA', value: 4},
                                                       {label: 'Telecoms', value: 5},
                                                       {label: 'LSBUD', value: 6},
                                                       {label: 'Document', value: 7}
                                                   ]}
                                                   initialValue={this.state.data.fileType}
                                />
                            );
                        }}
                    </MFormControl>
                </div>
                <div className="col-12 md:col-12">
                    <MFormControl label={'File Info'} id={'FileInfoDropdown'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MDropDown<number> id="FileInfoSelection"
                                                   disabled={false}
                                                   onChange={this.onFileInfoChange}
                                                   items={this.state.fileInfoOptions}
                                                   initialValue={dropdownInitialValue}
                                />
                            );
                        }}
                    </MFormControl>
                    {this.state.data.fileType === 7 && <MFormControl label={'File Name'} id={'FileNameText'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MTextBox id="FileNameEntry"
                                          maxLength={200}
                                          disabled={false}
                                          onChange={e => this.safeSetState((draft: State) => draft.data.fileName = e!.toString())}
                                />
                            );
                        }}
                    </MFormControl>}
                </div>
                <div className="col-12 md:col-12">
                    <MFormControl label={'Expiry Date'} id={'expirydate'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MCalendar id="FileExpiryDate"
                                           ref={this.m_dateRef}
                                           initialValue={this.state.data.expiryDate!}
                                           disabled={false}
                                           className={fieldClasses}
                                           onChange={e => this.safeSetState((draft: State) => draft.data.expiryDate = e!)}
                                />
                            );
                        }}
                    </MFormControl>
                </div>
                <div>
                    <input type="file" onChange={this.onFileChange} accept={'.pdf,.doc,.docx,.xls,.xlsx'}/>
                </div>
            </div>);
    }

    private getFooterTemplate(): ReactNode {
        return (
            <div>
                <MButton label="OK"
                         color="success"
                         icon={MOkIconName}
                         disabled={!this.isUploadAvailable()}
                         onClick={this.ok_Click}/>
                <MButton label="Cancel"
                         icon={MCancelIconName}
                         disabled={false}
                         onClick={this.hide}/>
            </div>
        );
    }

    private onFileTypeChange(newValue: MNullable<number>) {
        this.safeSetState(
            (draft: State) => draft.data!.fileType = newValue!);

        this.setFileInfoOptions(newValue!);
    }

    private onFileInfoChange(newValue: MNullable<number>) {
        this.safeSetState(
            (draft: State) => draft.data!.fileInfo = draft.fileInfoOptions[newValue!].label);

        if((this.state.data.fileType === 4 || this.state.data.fileType === 6) && newValue === 1) {
            this.safeSetState((draft: State) => draft.data!.searchPositive = true); //LSBUD or BAPA file
        } else if (this.state.data.fileType === 7) {
            var initialDate = new Date();
            if(newValue === 1){ //SSR document
                initialDate.setDate(initialDate.getDate() + 28)
            } else{
                initialDate.setFullYear(initialDate.getFullYear() + 2);
            }
            this.safeSetState((draft: State) => draft.data.expiryDate = initialDate);
            this.m_dateRef.current!.setValue(initialDate);
        } else {
            this.safeSetState((draft: State) => draft.data!.searchPositive = false);
        }
    }

    private onFileChange(event: React.ChangeEvent<HTMLInputElement>) {
        const fileTypes = ['.pdf','.doc','.docx','.xls','.xlsx'];

        if(event.target.files?.length) {
            const file = event.target.files[0];
            if(file.size <= SafeDigFile.MaxFileSizeMb * 1024 * 1024) {
                if(fileTypes.some(s => event.target.files![0].name.toLowerCase().endsWith(s))) {
                    this.safeSetState(
                        (draft: State) => draft.data!.file = event.target.files![0]);
                } else {
                    this.safeSetState(
                        (draft: State) => draft.data!.file = undefined);
                }
            } else {
                alert(`Files must be less than ${SafeDigFile.MaxFileSizeMb}Mb in size. Please rescale any images in the file and try again.`);
            }
        } else {
            this.safeSetState(
                (draft: State) => draft.data!.file = undefined);
        }
    }

    private isUploadAvailable() {
        return (this.state.data.fileType === 7 || (this.props.uploadPermission && !this.props.jobComplete)) &&
            !this.state.uploading &&
            this.state.data.fileType !== undefined &&
            this.state.data.fileInfo !== undefined &&
            this.state.data!.file !== undefined &&
            this.state.data!.fileName!.length > 0;
    }

    private setFileInfoOptions(fileNameValue: number) {
        let infoOptions: MDropDownItem<number>[] = [];
        let defaultIndex: number = 0;

        switch(fileNameValue) {
            case 1: //Gas
                infoOptions = [{label: 'Gas', value: 0}];
                break;
            case 2: //Electric
                infoOptions = [{label: 'Electric', value: 0}];
                break;
            case 3: //Water
                infoOptions = [{label: 'Water', value: 0}];
                break;
            case 4: //BAPA
                infoOptions = [
                    {label: 'BAPA Negative', value: 0},
                    {label: 'BAPA Positive', value: 1}
                ];
                defaultIndex = 1;
                break;
            case 5: //Telecoms
                infoOptions = [{label: 'Telecoms', value: 0}];
                break;
            case 6: //LSBUD
                infoOptions = [
                    {label: 'Line Search Negative', value: 0},
                    {label: 'Line Search Positive (ZOI)', value: 1}
                ];
                defaultIndex = 1;
                break;
            case 7: //Document
                infoOptions = [
                    {label: 'Safe Dig Document', value: 0},
                    {label: 'SSR', value: 1},
                    {label: 'Hot Job Document', value: 2},
                    {label: 'Job Pack', value: 3},
                    {label: 'TM Plan', value: 4},
                    {label: 'Permit', value: 5}
                ];
                break;
            default:
                break;
        }

        this.safeSetState((draft: State) => {
            draft.fileInfoOptions = infoOptions;
            draft.data.fileInfo = infoOptions[defaultIndex].label;
        });
    }

    /** */
    public render(): ReactElement {

        return (
            <Dialog title={this.state.pageTitle}
                    content={this.getDialogTemplate()}
                    icon={this.UploadIcon}
                    footer={this.getFooterTemplate()}
                    position={this.state.position}
                    canClose={false}
                    closeOnEscape={false}
                    visible={this.state.visible}
                    onHide={this.dialog_onHide}/>
        );

    }

}

export default FileUploadDialog;
